import { API, EscalationService } from 'core';
import useQueryParams from 'core/hooks/useQueryParams';
import useDebounce from 'hooks/useDebounce';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';

const DEFAULT_PAGE_SIZE = 5;

const useGetEscalationPolicies = ({ onSuccess }: { onSuccess?: () => void }) => {
  const query = useQueryParams();
  const filterQueryParam = query.get('entity_owner') ?? '';

  const ownerId = API.config.teamId;
  const _esclationService = new EscalationService();
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchInputTerm, setSearchInputTerm] = useState<string>('');
  const searchTerm = useDebounce(searchInputTerm, 500);

  const {
    data: escalationPolicies,
    isLoading: isLoadingEscalationPolicies,
    isRefetching: isRefetchingEscalationPolicies,
    refetch: refetchEscalationPolicies,
  } = useQuery({
    queryKey: [
      'escalationPolicies',
      ownerId,
      pageNumber + 1,
      pageSize,
      filterQueryParam,
      searchTerm,
    ],
    queryFn: async () => {
      try {
        const { data } = await _esclationService.getAllWithPagination(
          ownerId,
          pageNumber + 1,
          pageSize,
          filterQueryParam,
          searchTerm,
        );
        return data;
      } catch (error) {
        console.error('Failed to fetch escalation policies:', error);
        throw error;
      }
    },
    onSuccess: data => {
      setTotalCount(data.meta.total_count);
      onSuccess?.();
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const prevRef = useRef({
    searchTerm: '',
    filterQueryParam: '',
    pageSize: DEFAULT_PAGE_SIZE,
  });

  useEffect(() => {
    const prev = prevRef.current;
    const filtersChanged =
      prev.searchTerm !== searchTerm || prev.filterQueryParam !== filterQueryParam;
    const sizeChanged = prev.pageSize !== pageSize;
    if (filtersChanged || sizeChanged) {
      prevRef.current = { searchTerm, filterQueryParam, pageSize };
      if (pageNumber !== 0) {
        setPageNumber(0);
        return;
      }
    }
    refetchEscalationPolicies();
  }, [searchTerm, filterQueryParam, pageSize, pageNumber]);

  return {
    escalationPolicies,
    filterQueryParam,
    query,
    isLoadingEscalationPolicies,
    isRefetchingEscalationPolicies,
    pageNumber,
    pageSize,
    searchInputTerm,
    totalCount,
    setPageNumber,
    setPageSize,
    setSearchInputTerm,
  };
};

export default useGetEscalationPolicies;
